<template>
    <div ref="wrapper" class="datetime-picker">
        <input ref="picker" type="text" :value="displayValue" readonly class="date-input" @click="openCalendar">

        <!-- Teleport only on mobile, otherwise render inline -->
        <Teleport v-if="isMobile" to="body">
            <div class="datetime-picker-modal" :class="{ open: opened }" @click.stop>
                <div class="datetime-picker-content">
                    <div ref="modalWrapper" />
                    <ul class="time-picker-dropdown">
                        <li v-for="time in times" :key="time.value" :ref="time.value === displayTimeValue ? 'selectedTime' : null"
                            @mousedown.prevent="selectTime(time.value)" class="time-picker-option"
                            :class="{ 'selected': time.value === displayTimeValue }"
                        >
                            {{ time.label }}
                        </li>
                    </ul>
                </div>
                <button type="button" class="datetime-picker-confirm" @click="confirmSelection">
                    {{ $t('global.validate') }}
                </button>
            </div>
        </Teleport>
        <div v-else class="datetime-picker-modal" :class="{ open: opened }" @click.stop>
            <div class="datetime-picker-content">
                <div ref="modalWrapper" />
                <ul class="time-picker-dropdown">
                    <li v-for="time in times" :key="time.value" :ref="time.value === displayTimeValue ? 'selectedTime' : null"
                        @mousedown.prevent="selectTime(time.value)" class="time-picker-option"
                        :class="{ 'selected': time.value === displayTimeValue }"
                    >
                        {{ time.label }}
                    </li>
                </ul>
            </div>
            <button type="button" class="datetime-picker-confirm" @click="confirmSelection">
                {{ $t('global.validate') }}
            </button>
        </div>
    </div>
</template>

<script>
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { French } from 'flatpickr/dist/l10n/fr.js';
import Teleport from 'vue2-teleport';

export default {
    components: {
        Teleport
    },
    props: {
        value: {
            type: Date,
            default: null
        },
        withTime: {
            type: Boolean,
            default: false
        },
        maxDate: {
            type: Date,
            default: null
        },
        minDate: {
            type: Date,
            default: null
        },
        timeValue: {
            type: String,
            default: null
        },
        displayTimeValue: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            fp: null,
            selectedDate: this.value,
            opened: false,
            times: [],
            externalOpenHandler: null,
            isMobile: false, // track whether we're on mobile
        };
    },
    computed: {
        displayValue() {
            const date = this.selectedDate ? new Date(this.selectedDate) : new Date();
            return date.toLocaleDateString('fr-FR', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit'
            });
        }
    },
    watch: {
        value(newVal) {
            this.selectedDate = newVal;
        },
        minDate(newVal) {
            if (this.fp) this.fp.set('minDate', newVal || new Date());
        },
        maxDate(newVal) {
            if (this.fp) this.fp.set('maxDate', newVal || null);
        }
    },
    mounted() {
        // initial mobile check and add resize listener
        this.updateIsMobile();
        window.addEventListener('resize', this.updateIsMobile);

        // Defer initialization so Teleport content (modalWrapper) exists in the DOM on mobile
        this.$nextTick(() => {
            this.initFlatpickr();
        });

        this.generateTimes();
        this.externalOpenHandler = (e) => {
            if (e.detail !== this && this.opened) {
                this.opened = false;
                if (this.fp) this.fp.close();
            }
        };
        window.addEventListener('datetime-picker-opened', this.externalOpenHandler);
        document.addEventListener('click', this.onClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onClickOutside);
        if (this.externalOpenHandler) {
            window.removeEventListener('datetime-picker-opened', this.externalOpenHandler);
            this.externalOpenHandler = null;
        }
        if (this.fp) {
            this.fp.destroy();
            this.fp = null;
        }
        window.removeEventListener('resize', this.updateIsMobile);
    },
    methods: {
        updateIsMobile() {
            // change the breakpoint to match your CSS (currently 500px used in styles)
            const newVal = window.innerWidth <= 500;
            if (newVal !== this.isMobile) {
                this.isMobile = newVal;
                // Wait for Teleport to render/unrender its content, then relocate
                this.$nextTick(() => this.relocateCalendar());
            } else {
                this.isMobile = newVal;
            }
        },

        initFlatpickr() {
            // If already initialized, do nothing
            if (this.fp) {
                // Ensure the display value is correct
                this.$refs.picker.value = this.displayValue;
                return;
            }

            const appendTarget = this.$refs.modalWrapper || this.$refs.wrapper;

            this.fp = flatpickr(this.$refs.picker, {
                dateFormat: 'Y-m-d',
                clickOpens: false,
                enableTime: false,
                minDate: this.minDate || new Date(),
                maxDate: this.maxDate || null,
                locale: (window.applocale === 'fr' || navigator.language.startsWith('fr')) ? French : undefined,
                appendTo: appendTarget,
                inline: true,
                monthSelectorType: 'static',
                onOpen: () => {
                    const dt = this.selectedDate ? new Date(this.selectedDate) : new Date();
                    this.fp.setDate(dt, false);
                    this.$refs.picker.value = this.displayValue;
                },
                onChange: (selectedDates) => {
                    const dt = selectedDates[0];
                    this.selectedDate = dt;
                    this.$emit('input', dt);
                    this.$refs.picker.value = this.displayValue;
                },
                onReady: () => {
                    this.$refs.picker.value = this.displayValue;
                }
            });

            // Ensure initial input display value
            this.$refs.picker.value = this.displayValue;
        },

        relocateCalendar() {
            // If flatpickr exists, move its calendar container to the correct wrapper when switching mobile/desktop
            if (!this.fp || !this.fp.calendarContainer) return;

            const target = this.$refs.modalWrapper || this.$refs.wrapper;
            if (target && this.fp.calendarContainer.parentNode !== target) {
                target.appendChild(this.fp.calendarContainer);
            }
        },

        openCalendar() {
            window.dispatchEvent(new CustomEvent('datetime-picker-opened', { detail: this }));
            if (!this.opened) {
                this.opened = true;
                if (this.fp) this.fp.open();
                this.$nextTick(() => {
                    if (this.$refs.selectedTime && this.$refs.selectedTime[0]) {
                        this.$refs.selectedTime[0].scrollIntoView({
                            block: 'nearest',
                            inline: 'nearest'
                        });
                    }
                });
            }
        },
        generateTimes() {
            const times = [];
            for (let h = 0; h < 24; h++) {
                for (let m = 0; m < 60; m += 15) {
                    const hour = String(h).padStart(2, '0');
                    const min = String(m).padStart(2, '0');
                    times.push({
                        value: `${hour}:${min}`,
                        label: `${hour}h${min}`
                    });
                }
            }
            this.times = times;
        },
        onClickOutside(e) {
            if (!this.opened) return;
            const wrapper = this.$refs.wrapper;
            if (wrapper && !wrapper.contains(e.target)) {
                this.opened = false;
                if (this.fp) this.fp.close();
            }
        },

        selectTime(time) {
            this.displayTimeValue = time;
            this.$emit('input-time', time);
        },

        confirmSelection() {
            this.opened = false;
            if (this.fp) this.fp.close();
        },
    }
};
</script>

<style lang="scss">
@import "~styles/_variables";
.datetime-picker {
    position: relative;
    display: inline-block;

    .date-input {
        cursor: pointer;
        font-weight: 400;
        width: 70px;
    }

    .date-input:focus {
        outline: none;
    }
}

// Modal dropdown styling for the date picker
.datetime-picker-modal {
    position: absolute;
    top: 100%;
    left: 24px;
    margin-top: 6px;
    background: #fff;
    border: 1px solid rgba(0,0,0,0.1);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    border-radius: 6px;
    padding: 8px;
    z-index: 20;
    display: none;
    flex-direction: column;

    @media screen and (min-width: 1400px) {
        left: 70px;
    }

    @media screen and (max-width: 1024px) {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        max-width: 100vw;
        margin-top: 0;
        box-shadow: 0 4px 24px rgba(0,0,0,0.25);
        padding: 6px;
    }
    .flatpickr-calendar {
        font-size: 12px;
        width: 250px;
        .flatpickr-months {
            .flatpickr-month {
                .flatpickr-current-month {
                    display: flex;
                    align-items: center;
                    .cur-year {
                        font-size: 13px;
                        font-weight: 600;
                        color: $primary-color-dark;
                    }
                    .cur-month {
                        flex-grow: 1;
                        font-weight: 600;
                        color: $primary-color-dark;
                    }
                }
            }
        }
        .flatpickr-innerContainer {
            .flatpickr-rContainer {
                .flatpickr-weekdays {

                }
                .flatpickr-days {
                    width: auto;
                    .dayContainer {
                        width: 250px;
                        min-width: auto;
                        max-width: auto;
                        .flatpickr-day {
                            max-width: 35px;
                            height: 35px;
                            line-height: 35px;
                        }
                        .flatpickr-day.selected {
                            background: $primary-color;
                            color: #fff;
                            font-weight: 300;
                        }
                    }
                }
            }
        }
    }
    .flatpickr-calendar:before, .flatpickr-calendar:after {
        border: none;
    }

    .time-picker-dropdown {
        position: relative;
        display: inline-block;
        z-index: 100;
        border-radius: 5px;
        width: 100%;
        background: #fff;
        border: 0;
        max-height: 273px;
        overflow-y: auto;
        padding: 0;
        list-style: none;
        box-sizing: border-box;
        margin: 2px 0px 0px 5px;
        box-shadow: 0 0 0 #e6e6e6, -1px 0 0 #e6e6e6, 0 1px 0 #e6e6e6, 0 -1px 0 #e6e6e6, 0 3px 13px rgba(0, 0, 0, 0.08);
        font-size: 12px;
        .time-picker-option {
            padding: 8px 12px;
            cursor: pointer;
        }

        .time-picker-option:hover {
            background: #eee;
        }

        .time-picker-option.selected {
            background: $primary-color;
            color: #fff;
        }
    }
}

.datetime-picker-modal.open {
    display: flex;
}

.datetime-picker-content {
    display: flex;
    align-items: flex-start;
}

.datetime-picker-confirm {
    width: 100%;
    padding: 10px 16px;
    margin-top: 8px;
    background: $primary-color;
    color: #fff;
    border: none;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: darken($primary-color, 10%);
    }
}

</style>


// WEBPACK FOOTER //
// resources/assets/js/components/date-time-picker/DateTimePicker.vue